import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FolderOpen, Loader2, Sparkles, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ALL_OPTIONS } from "../../lib/catalog";
import { api, errMsg, imgUrl } from "../../lib/api";

const Thumb = ({ p, onDelete, onFestive, i }) => (
  <div className="group relative overflow-hidden rounded-xl border border-[#D4AF37]/20" data-testid={`admin-photo-${i}`}>
    <img src={imgUrl(p.url)} alt={p.title} className="aspect-square w-full object-cover" loading="lazy" />
    {onFestive && (
      <button onClick={onFestive} className={`absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[11px] font-semibold ${p.festive ? "bg-gold-foil text-[#240620]" : "bg-[#190317]/80 text-[#F3E5AB]"}`} aria-pressed={p.festive} data-testid={`admin-photo-festive-${i}`}>
        <Sparkles size={11} /> {p.festive ? "Festive" : "Add to festive"}
      </button>
    )}
    {onDelete && (
      <button onClick={onDelete} className="absolute right-2 top-2 flex h-9 w-9 items-center justify-center rounded-full bg-[#190317]/80 text-[#ff8fb3] opacity-100 sm:opacity-0 sm:group-hover:opacity-100" data-testid={`admin-photo-delete-${i}`} aria-label="Delete photo">
        <Trash2 size={16} />
      </button>
    )}
  </div>
);

export const PhotoManager = () => {
  const qc = useQueryClient();
  const [slug, setSlug] = useState(ALL_OPTIONS[0].slug);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const { data, isLoading } = useQuery({ queryKey: ["admin-photos", slug], queryFn: () => api.get(`/admin/photos/${slug}`).then((r) => r.data) });
  const uploads = Array.isArray(data?.uploads) ? data.uploads : [];
  const folder = Array.isArray(data?.folder) ? data.folder : [];
  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-photos", slug] }); qc.invalidateQueries({ queryKey: ["photos", slug] }); };

  const upload = async () => {
    const files = Array.from(fileRef.current?.files || []);
    if (!files.length) return toast.error("Choose one or more photos first");
    const fd = new FormData();
    fd.append("category", slug);
    if (title.trim()) fd.append("title", title.trim());
    files.forEach((f) => fd.append("files", f));
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/photos", fd);
      toast.success(`${res.uploaded} photo(s) uploaded`);
      fileRef.current.value = "";
      setTitle("");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this photo from the gallery?")) return;
    try {
      await api.delete(`/admin/photos/${id}`);
      toast.success("Photo removed");
      refresh();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const toggleFestive = async (p) => {
    try {
      await api.patch(`/admin/photos/${p.id}/festive`, { festive: !p.festive });
      toast.success(p.festive ? "Removed from Special Festive Offers" : "Added to Special Festive Offers");
      refresh();
      qc.invalidateQueries({ queryKey: ["photos", "festive-offers"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12">
      <div className="space-y-5 rounded-[24px] border border-[#D4AF37]/25 bg-[#330B2E]/70 p-6 lg:col-span-4">
        <h2 className="font-display text-3xl">Add photos</h2>
        <div className="space-y-2">
          <Label className="text-[#F3E5AB]">Category</Label>
          <Select value={slug} onValueChange={setSlug}>
            <SelectTrigger className="h-12 rounded-xl border-[#D4AF37]/25 bg-[#190317]/60" data-testid="admin-category-select"><SelectValue /></SelectTrigger>
            <SelectContent data-lenis-prevent className="max-h-72 border-[#D4AF37]/30 bg-[#1d051a] text-[#FFF8EF]">
              {ALL_OPTIONS.map((o) => <SelectItem key={o.slug} value={o.slug}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="adm-title" className="text-[#F3E5AB]">Title (optional)</Label>
          <Input id="adm-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Rose & jasmine mandap" className="h-12 rounded-xl border-[#D4AF37]/25 bg-[#190317]/60" data-testid="admin-title-input" />
        </div>
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-2xl border border-dashed border-[#D4AF37]/40 p-6 text-center text-sm text-[#E8D8C8] hover:border-[#D4AF37]">
          <UploadCloud className="text-[#D4AF37]" />
          Choose photos (JPG, PNG, WEBP · up to 15 MB each)
          <input ref={fileRef} type="file" accept="image/*" multiple className="mt-2 w-full text-xs" data-testid="admin-file-input" />
        </label>
        <button onClick={upload} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-full bg-gold-foil py-3.5 text-sm font-semibold text-[#240620] disabled:opacity-60" data-testid="admin-upload-btn">
          {busy && <Loader2 size={16} className="animate-spin" />} Upload
        </button>
        <p className="text-xs leading-relaxed text-[#E8D8C8]">
          Your logo is added as a watermark to every uploaded photo automatically. Tap "Add to festive" on any photo to show it in Special Festive Offers with a festive badge.
        </p>
        <p className="flex gap-2 text-xs leading-relaxed text-[#E8D8C8]"><FolderOpen size={14} className="mt-0.5 shrink-0 text-[#D4AF37]" /> You can also copy image files into the project folder <code className="text-[#F3E5AB]">backend/gallery/{slug}/</code>.</p>
      </div>
      <div className="lg:col-span-8">
        <h2 className="font-display text-3xl">Uploaded <span className="text-[#D4AF37]">({uploads.length})</span></h2>
        {isLoading ? <Loader2 className="mt-6 animate-spin text-[#F3E5AB]" /> : uploads.length === 0 ? (
          <p className="mt-4 text-sm text-[#E8D8C8]" data-testid="admin-no-uploads">No uploaded photos yet in this category.</p>
        ) : (
          <div className="mt-5 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
            {uploads.map((p, i) => <Thumb key={p.id} p={p} i={i} onDelete={() => remove(p.id)} onFestive={() => toggleFestive(p)} />)}
          </div>
        )}
        {folder.length > 0 && (
          <>
            <h3 className="mt-10 font-display text-2xl">From project folder ({folder.length})</h3>
            <div className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">{folder.map((p, i) => <Thumb key={p.id} p={p} i={`f${i}`} />)}</div>
          </>
        )}
      </div>
    </div>
  );
};
